/**
 * Channel Categories Policy
 *
 * Side-effects for the test-channel category mapper (TV1-1887). Owns three
 * concerns for the channel-detail "Categorías" tab:
 *
 * 1. **Readmodel subscription (signal + refetch).** While the categories tab is
 *    open, subscribe to `systems_integrations_channel_categories_readmodel`
 *    filtered by `integrationId` in **signal mode**
 *    ( `{ _snapshot: false, _changes: 'signal' }` ). pv3 signal-mode
 *    subscriptions deliver a bare `{ type: 'signal' }` with NO data and do NOT
 *    auto-refetch — so this policy refetches the readmodel itself ( debounced )
 *    on every signal, plus an initial fetch when the tab opens. Dispatches
 *    `categories/set` ( source `api` ).
 * 2. **Map / Unmap.** A user-sourced `categories/map` / `categories/unmap`
 *    optimistically updates the row AND enqueues a `CategoryWriteIntent` in
 *    `pendingCategoryWrites`. This policy drains that queue — dispatching the
 *    backend `MapChannelCategory` / `UnmapChannelCategory` command via the
 *    in-process client library, then clearing the drained intents — and the
 *    subscription signal reconciles the row from the readmodel.
 *
 *    The pending queue is load-bearing: CDSE `onStateChange` fires for EVERY
 *    state change (incl. the `api` subscription snapshot) with NO command
 *    source, so the policy can NOT infer user intent from a `prev`/`next` row
 *    diff. Draining `pendingCategoryWrites` — which only the user-sourced
 *    handlers populate — guarantees an `api` `categories/set` of already-mapped
 *    rows (e.g. exact-match auto-maps) is never re-pushed as a manual mapping.
 * 3. **ML category search.** A user-sourced `categories/search/set` triggers a
 *    debounced `GET /item-publishing/mercadolibre-categories/search` ( filtered
 *    to `isLeaf: true` ), dispatching `categories/search/results/set`
 *    ( source `api` ).
 * 4. **AI-mapping run awaiting review** ( TV1-2113, S7 ). While the tab is open,
 *    look up the `map-channel-categories` operation run for this integration
 *    sitting in `ready` and publish its id, so the tab can raise a "your mapping
 *    proposal is ready to review" banner.
 *
 *    This needs its OWN subscription on the operation-runs lifecycle: a run
 *    reaching `ready` changes no category row, so the categories signal never
 *    fires for it. And it cannot ride the navbar batch-processes dropdown —
 *    that dropdown shows work that is RUNNING, and a run awaiting review is
 *    precisely not running.
 *
 * Follows the subscription + dispatch pattern of `channelDetailLoadPolicy` and
 * the channel command policies ( `channelDetailSaveBillingPolicy` ).
 */

import { Policy } from '@commandDrivenStateEngine/core/policies';
import { ClientLibraryType } from '@bett3r-dev/teselly-client-library';
import {
  ChannelCategoriesReadmodelType,
  ChannelCategorySuggestionType,
  ChannelCategoryMappingConfidenceType,
  ChannelCategoryMappingSourceType,
  MercadolibreCategorySearchHitType
} from '@bett3r-dev/teselly-domain';
import {
  ChannelsState,
  ChannelCategoryRow,
  CategoryWriteIntent,
  categoryIntentKey
} from '../state';
import { ChannelsCommand } from '../commands';

const SIGNAL_DEBOUNCE_MS = 200;
const SEARCH_DEBOUNCE_MS = 300;
const SEARCH_RESULT_LIMIT = 5;
const MIN_SEARCH_LENGTH = 1;

/**
 * The mapping-target filter for every ML category search this policy makes:
 * leaves only (roots and mid-level nodes are not publishable targets) and the
 * canonical taxonomy site only (TV1-2113, D8).
 *
 * The site literal is duplicated from `CANONICAL_TAXONOMY_SITE` in
 * `@bett3r-dev/teselly-domain` rather than imported. That package's root entry
 * is a TYPE-only import here today; turning it into a value import to pull one
 * string would drag the whole domain index into the web-app bundle and into its
 * CJS/ESM interop surface — a real cost for a three-character constant. The
 * domain constant remains the source of truth and carries the reasoning; this is
 * its mirror, deliberately, the same way `ChannelInfo.tsx` mirrors the connector
 * allow-list.
 */
// NOT `as const`: the client library's `filter` parameter is a MUTABLE type, so a
// readonly tuple is rejected (TS4104). Built fresh per call for the same reason —
// sharing one array across two concurrent searches would let the query layer
// mutate a value the other call is still reading.
const categorySearchFilter = () => [
  { field: 'isLeaf', op: 'eq', value: true },
  { field: 'site', op: 'eq', value: 'MLA' }
];

interface SubscriptionHandle {
  close: () => void;
}

/**
 * The `map-channel-categories` operation type string. It is the operation the
 * "Mapear con IA" button proposes and the one whose `ready` run the banner
 * watches for.
 */
export const MAP_CHANNEL_CATEGORIES_OPERATION = 'map-channel-categories';

/**
 * Map a backend readmodel row to the client `ChannelCategoryRow` shape.
 *
 * `tesellyCategoryPath` is DERIVED, not projected: the channel-categories
 * readmodel stores only the mapped category's id and leaf name, while the full
 * `Root > … > Leaf` breadcrumb the seller needs to tell two same-named leaves
 * apart lives on the row's own `suggestions`. When the mapped id is one of the
 * suggested ids — which it is for every `exact-match` and `ai` mapping, and for
 * any manual pick made from a suggestion chip — we recover the path from there.
 * A category mapped through the free-text picker has no suggestion to recover
 * it from and falls back to the leaf name in the UI.
 */
const toCategoryRow = ( doc: ChannelCategoriesReadmodelType ): ChannelCategoryRow => {
  const suggestions = ( doc.suggestions ?? []) as ChannelCategorySuggestionType[];
  const mappedSuggestion = doc.tesellyCategoryId
    ? suggestions.find(( s ) => s.tesellyCategoryId === doc.tesellyCategoryId )
    : undefined;
  return {
    externalId: doc.externalId,
    name: doc.name,
    path: doc.path,
    tesellyCategoryId: doc.tesellyCategoryId ?? null,
    tesellyCategoryName: doc.tesellyCategoryName ?? null,
    tesellyCategoryPath: mappedSuggestion?.tesellyCategoryPath ?? null,
    isMapped: doc.isMapped ?? false,
    mappingSource: ( doc.mappingSource ?? null ) as ChannelCategoryMappingSourceType | null,
    mappingConfidence:
      ( doc.mappingConfidence ?? null ) as ChannelCategoryMappingConfidenceType | null,
    mappingRationale: doc.mappingRationale ?? null,
    isUnmappable: doc.isUnmappable ?? false,
    suggestions
  };
};

const isCategoriesTabOpen = ( state: ChannelsState ): boolean =>
  state.channelDetail.mode.type === 'view' &&
  state.channelDetail.activeTab === 'categories';

const currentIntegrationId = ( state: ChannelsState ): string | null =>
  state.channelDetail.mode.type === 'view'
    ? state.channelDetail.mode.integrationId
    : null;

export const createCategoriesPolicy = (
  backend: ClientLibraryType
): Policy<ChannelsState, ChannelsCommand> => {
  let subscription: SubscriptionHandle | null = null;
  let subscribedIntegrationId: string | null = null;
  let signalTimer: ReturnType<typeof setTimeout> | null = null;
  // Second subscription, on the operation-runs lifecycle, so the "ready for
  // review" banner appears the moment the run finishes computing. It is a
  // SEPARATE readmodel from the channel categories — a run reaching `ready`
  // changes no category row, so the categories signal would never fire for it.
  let runsSubscription: SubscriptionHandle | null = null;
  let runsSignalTimer: ReturnType<typeof setTimeout> | null = null;
  let searchTimer: ReturnType<typeof setTimeout> | null = null;
  let lastSearchQuery = '';
  // Intents (by `categoryIntentKey`) currently being written to the backend, so
  // an overlapping `onStateChange` invocation doesn't double-dispatch the same
  // pending write while its command is in flight.
  const inFlightWrites = new Set<string>();
  // Guards against double-dispatching a resync while its command is in flight.
  let inFlightResync = false;
  // Guards against concurrent "load more" fetches triggered by rapid scroll events.
  let inFlightLoadMore = false;
  // Set after RequestCategoriesResync returns so the next subscription signal
  // clears the spinner rather than clearing it immediately (the backend still
  // needs to run syncCatalog + ML vector searches before the new category appears).
  let resyncPendingClear = false;

  type DispatchFn = ( cmd: ChannelsCommand, meta: { source: string }) => void;

  const closeSubscription = () => {
    if ( signalTimer ) {
      clearTimeout( signalTimer );
      signalTimer = null;
    }
    if ( subscription ) {
      subscription.close();
      subscription = null;
    }
    if ( runsSignalTimer ) {
      clearTimeout( runsSignalTimer );
      runsSignalTimer = null;
    }
    if ( runsSubscription ) {
      runsSubscription.close();
      runsSubscription = null;
    }
    subscribedIntegrationId = null;
  };

  /**
   * Refetch the per-integration category catalog and dispatch the snapshot.
   */
  const fetchCategories = async ( integrationId: string, dispatch: DispatchFn ) => {
    try {
      const docs = await backend.queries.SystemsIntegrationsChannelCategories({ integrationId });
      dispatch(
        {
          type: 'integrations/channels/categories/set',
          payload: { categories: ( docs ?? []).map( toCategoryRow ) }
        },
        { source: 'api' }
      );
    } catch ( error ) {
      console.error( '[channels] Error loading channel categories:', error );
    } finally {
      dispatch(
        { type: 'integrations/channels/categories/loading/setCategories', payload: { isLoading: false }},
        { source: 'api' }
      );
    }
  };

  /**
   * Find the `map-channel-categories` run for `integrationId` that is awaiting
   * review, and publish its id (or `null`) into state.
   *
   * `operationType` + `status` are filtered SERVER-side; the integration is
   * matched CLIENT-side on `params.integrationId` because `params` is freeform
   * operation-defined JSON (`BaseSchema<any>`) and is not an indexed, filterable
   * column of the runs readmodel. The candidate set is tiny — the runs of ONE
   * operation type that are simultaneously awaiting review for one account — so
   * the client-side narrowing costs nothing.
   *
   * If more than one is ready (the seller proposed twice), the most recently
   * created wins: that is the proposal they are actually waiting on.
   */
  const fetchAiMappingRun = async ( integrationId: string, dispatch: DispatchFn ) => {
    try {
      const runs = await backend.queries.OperationsRuns({
        filter: [
          { field: 'operationType', op: 'eq', value: MAP_CHANNEL_CATEGORIES_OPERATION },
          { field: 'status', op: 'eq', value: 'ready' }
        ],
        sort: [{ field: 'createdAt', direction: 'desc' }]
      });
      const match = ( runs ?? []).find(
        ( run ) => run.params?.integrationId === integrationId
      );
      dispatch(
        {
          type: 'integrations/channels/categories/aiRun/set',
          payload: { runId: match?.id ?? null }
        },
        { source: 'api' }
      );
    } catch ( error ) {
      console.error( '[channels] Error loading AI category-mapping runs:', error );
    }
  };

  /**
   * Open a signal-mode subscription for `integrationId` and refetch on every
   * signal. pv3 signal mode delivers `{ type: 'signal' }` with no data, so the
   * consumer ( this policy ) is responsible for the refetch.
   */
  const subscribe = (
    integrationId: string,
    dispatch: DispatchFn,
    getState: () => ChannelsState
  ) => {
    closeSubscription();
    subscribedIntegrationId = integrationId;

    dispatch(
      { type: 'integrations/channels/categories/loading/setCategories', payload: { isLoading: true }},
      { source: 'api' }
    );

    // Clear the previous integration's banner SYNCHRONOUSLY. `fetchAiMappingRun`
    // is async, so without this the banner for the channel we just left would
    // linger over the new one until the query resolves — and clicking it would
    // navigate to a run about a different channel.
    dispatch(
      { type: 'integrations/channels/categories/aiRun/set', payload: { runId: null }},
      { source: 'api' }
    );

    // Initial load ( signal mode never sends a snapshot ).
    fetchCategories( integrationId, dispatch );
    fetchAiMappingRun( integrationId, dispatch );

    // Watch the operation-runs lifecycle so a run that finishes computing while
    // the tab is open raises the banner without a reload.
    runsSubscription = backend.subscriptions.OperationsRuns(
      { _snapshot: false, _changes: 'signal' },
      ( change ) => {
        if ( change?.type !== 'signal' ) return;
        if ( runsSignalTimer ) clearTimeout( runsSignalTimer );
        runsSignalTimer = setTimeout( async () => {
          runsSignalTimer = null;
          const state = getState();
          if (
            isCategoriesTabOpen( state ) &&
            currentIntegrationId( state ) === integrationId
          ) {
            await fetchAiMappingRun( integrationId, dispatch );
          }
        }, SIGNAL_DEBOUNCE_MS );
      }
    );

    subscription = backend.subscriptions.SystemsIntegrationsChannelCategories(
      { integrationId, _snapshot: false, _changes: 'signal' },
      ( change ) => {
        if ( change?.type !== 'signal' ) return;
        if ( signalTimer ) clearTimeout( signalTimer );
        signalTimer = setTimeout( async () => {
          signalTimer = null;
          // Only refetch while the tab is still on this integration.
          const state = getState();
          if (
            isCategoriesTabOpen( state ) &&
            currentIntegrationId( state ) === integrationId
          ) {
            await fetchCategories( integrationId, dispatch );
            // Clear the resync spinner now that the updated catalog is visible.
            if ( resyncPendingClear ) {
              resyncPendingClear = false;
              dispatch(
                { type: 'integrations/channels/categories/loading/setResyncing', payload: { isLoading: false }},
                { source: 'api' }
              );
            }
          }
        }, SIGNAL_DEBOUNCE_MS );
      }
    );
  };

  /**
   * Stable signature of the pending-write queue, so we only react when the user
   * actually enqueued (or the policy cleared) a map/unmap intent — never on an
   * `api` reconciliation of the category rows.
   */
  const pendingSignature = ( state: ChannelsState ): string =>
    state.channelDetail.pendingCategoryWrites.map( categoryIntentKey ).join( '|' );

  /**
   * Dispatch each pending intent to the backend, then clear the drained intents
   * (by key) from state. In-flight keys are tracked so an overlapping
   * `onStateChange` cannot double-dispatch a write while its command is pending.
   */
  const drainPendingWrites = async (
    intents: CategoryWriteIntent[],
    integrationId: string,
    dispatch: DispatchFn
  ): Promise<void> => {
    const drainedKeys: string[] = [];
    for ( const intent of intents ) {
      const key = categoryIntentKey( intent );
      inFlightWrites.add( key );
      // eslint-disable-next-line teselly/no-swallowed-command-dispatch -- category-mapping write; console.error operator log only (TV1-2010 waiver, not a rethrow).
      try {
        if ( intent.kind === 'map' ) {
          await backend.commands.MapChannelCategory({
            id: integrationId,
            body: {
              externalId: intent.externalId,
              tesellyCategoryId: intent.tesellyCategoryId,
              tesellyCategoryName: intent.tesellyCategoryName,
              source: 'manual'
            }
          });
        } else {
          await backend.commands.UnmapChannelCategory({
            id: integrationId,
            body: { externalId: intent.externalId }
          });
        }
      } catch ( error ) {
        console.error( '[channels] Error writing channel category mapping:', error );
      } finally {
        drainedKeys.push( key );
        inFlightWrites.delete( key );
      }
    }
    if ( drainedKeys.length ) {
      dispatch(
        { type: 'integrations/channels/categories/pending/clear', payload: { keys: drainedKeys }},
        { source: 'api' }
      );
    }
  };

  /**
   * Drain a pending "resync from channel" intent: dispatch the backend resync
   * command (which re-pulls the channel catalog, registers new categories,
   * recomputes suggestions, and exact-match auto-maps), then clear the intent.
   * The subscription signal reconciles the new rows. Only the user-sourced
   * `categories/resync` handler sets the flag, so `api` reconciliation can never
   * trigger a resync.
   */
  const drainPendingResync = async (
    integrationId: string,
    dispatch: DispatchFn
  ): Promise<void> => {
    inFlightResync = true;
    dispatch(
      { type: 'integrations/channels/categories/loading/setResyncing', payload: { isLoading: true }},
      { source: 'api' }
    );
    // eslint-disable-next-line teselly/no-swallowed-command-dispatch -- categories resync request; console.error operator log + spinner reset only (TV1-2010 waiver, not a rethrow).
    try {
      await backend.commands.RequestCategoriesResync({ id: integrationId, body: {}});
      // The command accepted the request but syncCatalog runs asynchronously
      // (ML vector searches included). Keep the spinner on until the readmodel
      // subscription signal confirms the new catalog is projected and visible.
      resyncPendingClear = true;
      // Safety valve: clear the spinner after 20 s if no signal arrives.
      setTimeout(() => {
        if ( resyncPendingClear ) {
          resyncPendingClear = false;
          dispatch(
            { type: 'integrations/channels/categories/loading/setResyncing', payload: { isLoading: false }},
            { source: 'api' }
          );
        }
      }, 20_000 );
    } catch ( error ) {
      console.error( '[channels] Error requesting channel categories resync:', error );
      dispatch(
        { type: 'integrations/channels/categories/loading/setResyncing', payload: { isLoading: false }},
        { source: 'api' }
      );
    } finally {
      dispatch(
        { type: 'integrations/channels/categories/resync/clear', payload: {}},
        { source: 'api' }
      );
      inFlightResync = false;
    }
  };

  return {
    name: 'channels-categories',

    init: ({ getState, dispatch }) => {
      // Cold-mount catch: if the detail view is already on the categories tab
      // ( direct nav / reload ) the subscription must start without waiting for
      // a tab-change transition.
      const state = getState();
      const integrationId = currentIntegrationId( state );
      if ( isCategoriesTabOpen( state ) && integrationId ) {
        subscribe( integrationId, dispatch, getState );
      }

      return () => {
        closeSubscription();
        if ( searchTimer ) {
          clearTimeout( searchTimer );
          searchTimer = null;
        }
      };
    },

    shouldReact: ( prev, next ) => {
      // Tab/integration entered or left categories.
      const prevOpen = isCategoriesTabOpen( prev );
      const nextOpen = isCategoriesTabOpen( next );
      if ( prevOpen !== nextOpen ) return true;
      if ( nextOpen && currentIntegrationId( prev ) !== currentIntegrationId( next )) return true;

      // Search query changed.
      if (
        prev.channelDetail.categorySearch.query !==
        next.channelDetail.categorySearch.query
      ) {
        return true;
      }

      // The user enqueued a map/unmap intent (or the policy cleared one).
      if ( pendingSignature( prev ) !== pendingSignature( next )) return true;

      // The user requested a resync (or the policy cleared it).
      if (
        prev.channelDetail.pendingCategoryResync !==
        next.channelDetail.pendingCategoryResync
      ) {
        return true;
      }

      // The user requested the next page of search results (infinite scroll).
      if (
        prev.channelDetail.categorySearch.pendingLoadMore !==
        next.channelDetail.categorySearch.pendingLoadMore
      ) {
        return true;
      }

      return false;
    },

    onStateChange: async ({ prev, next, ctx }) => {
      const { dispatch, getState } = ctx;

      // --- Subscription lifecycle ---------------------------------------
      const nextOpen = isCategoriesTabOpen( next );
      const integrationId = currentIntegrationId( next );

      if ( nextOpen && integrationId ) {
        if ( subscribedIntegrationId !== integrationId ) {
          subscribe( integrationId, dispatch, getState );
        }
      } else if ( !nextOpen && subscription ) {
        closeSubscription();
      }

      // --- ML category search ( debounced ) -----------------------------
      const query = next.channelDetail.categorySearch.query;
      if ( prev.channelDetail.categorySearch.query !== query ) {
        if ( searchTimer ) {
          clearTimeout( searchTimer );
          searchTimer = null;
        }
        lastSearchQuery = query;

        if ( query.trim().length < MIN_SEARCH_LENGTH ) {
          dispatch(
            { type: 'integrations/channels/categories/search/results/set', payload: { results: [], hasMore: false }},
            { source: 'api' }
          );
          dispatch(
            { type: 'integrations/channels/categories/loading/setSearch', payload: { isLoading: false }},
            { source: 'api' }
          );
        } else {
          dispatch(
            { type: 'integrations/channels/categories/loading/setSearch', payload: { isLoading: true }},
            { source: 'api' }
          );
          searchTimer = setTimeout( async () => {
            searchTimer = null;
            try {
              // Leaf-only, MLA-only, ONE pass (TV1-2113 S1).
              //
              // This used to run a second UNFILTERED pass whenever the leaf pass
              // returned fewer than the limit, "so mid-level ML categories are
              // not silently excluded". Only `isLeaf || isRoot` documents are
              // ever indexed, so that pass could only ever return ROOTS — and
              // here, in the MANUAL mapping picker, that is worse than in the
              // auto-map path: it invited a human to deliberately choose an
              // unpublishable root and gave them no way to tell. The server-side
              // twin of this bug is documented on
              // `channelCategoriesProvision.policy.ts#searchLeafCandidates`.
              const leafResponse = await backend.queries.ItemPublishingMercadolibreCategoriesSearch({
                keyword: query,
                filter: categorySearchFilter(),
                limit: SEARCH_RESULT_LIMIT
              });
              if ( lastSearchQuery !== query ) return;

              const results = ( leafResponse.categories ?? []) as MercadolibreCategorySearchHitType[];

              dispatch(
                {
                  type: 'integrations/channels/categories/search/results/set',
                  payload: { results, hasMore: results.length >= SEARCH_RESULT_LIMIT }
                },
                { source: 'api' }
              );
            } catch ( error ) {
              console.error( '[channels] Error searching ML categories:', error );
              if ( lastSearchQuery === query ) {
                dispatch(
                  { type: 'integrations/channels/categories/search/results/set', payload: { results: [], hasMore: false }},
                  { source: 'api' }
                );
              }
            } finally {
              if ( lastSearchQuery === query ) {
                dispatch(
                  { type: 'integrations/channels/categories/loading/setSearch', payload: { isLoading: false }},
                  { source: 'api' }
                );
              }
            }
          }, SEARCH_DEBOUNCE_MS );
        }
      }

      // --- Infinite scroll: load next page of search results ---------------
      const { categorySearch } = next.channelDetail;
      if (
        categorySearch.pendingLoadMore &&
        !prev.channelDetail.categorySearch.pendingLoadMore &&
        categorySearch.hasMore &&
        !inFlightLoadMore
      ) {
        inFlightLoadMore = true;
        dispatch(
          { type: 'integrations/channels/categories/loading/setSearchLoadingMore', payload: { isLoading: true }},
          { source: 'api' }
        );
        try {
          // Same filter as the first page — an unfiltered "load more" would make
          // page 2 of the picker structurally different from page 1, quietly
          // reintroducing roots two scrolls down.
          const response = await backend.queries.ItemPublishingMercadolibreCategoriesSearch({
            keyword: categorySearch.query,
            filter: categorySearchFilter(),
            offset: categorySearch.offset,
            limit: SEARCH_RESULT_LIMIT
          });
          dispatch(
            {
              type: 'integrations/channels/categories/search/results/append',
              payload: {
                results: ( response.categories ?? []) as MercadolibreCategorySearchHitType[],
                hasMore: ( response.categories ?? []).length >= SEARCH_RESULT_LIMIT
              }
            },
            { source: 'api' }
          );
        } catch ( error ) {
          console.error( '[channels] Error loading more ML categories:', error );
          dispatch(
            { type: 'integrations/channels/categories/search/results/append', payload: { results: [], hasMore: false }},
            { source: 'api' }
          );
        } finally {
          inFlightLoadMore = false;
          dispatch(
            { type: 'integrations/channels/categories/loading/setSearchLoadingMore', payload: { isLoading: false }},
            { source: 'api' }
          );
        }
      }

      // --- Backend map / unmap (drain the pending-write queue) ----------
      // Only user-enqueued intents are pushed to the backend. An `api`
      // `categories/set` reconciliation never enqueues, so it can never be
      // mistaken for a user edit (this is the C1 fix — TV1-1887 review).
      const pending = next.channelDetail.pendingCategoryWrites;
      if ( pending.length && integrationId ) {
        const toProcess = pending.filter(
          ( intent ) => !inFlightWrites.has( categoryIntentKey( intent ))
        );
        if ( toProcess.length ) {
          await drainPendingWrites( toProcess, integrationId, dispatch );
        }
      }

      // --- Resync from channel (drain the pending-resync intent) --------
      if ( next.channelDetail.pendingCategoryResync && integrationId && !inFlightResync ) {
        await drainPendingResync( integrationId, dispatch );
      }
    }
  };
};
